import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { trackCta } from "@/lib/analytics";
import {
  CATEGORY_SLUGS,
  CATEGORY_LABEL_KEYS,
  CARDS,
  filteredCards,
  findCardBySlug,
  cardPublicUrl,
} from "@/data/trainingCards";
import CardTile from "@/components/cards/CardTile";
import CardModal from "@/components/cards/CardModal";
import { StoicosCardSections } from "@/components/cards/StoicosCardGrid";
import { StoicTrainingSection } from "@/components/cards/StoicTrainingGrid";
import { QUOTE_SECTION, pickLang } from "@/data/stoicosCards";

/**
 * /stoic-training-cards — Library page.
 *
 *   • Hero (eyebrow / title / lede / intro)
 *   • Category filter tabs (All · Virtue · Emotion · Philosophy)
 *   • Grid of card tiles
 *   • Lightbox modal opened on click — exposes download + share actions
 *
 * Deep-link support: /stoic-training-cards/:slug opens the modal directly.
 * This is how social shares (LinkedIn, X, Facebook, WhatsApp) embed previews
 * of a specific card — the share URL points to /stoic-training-cards/<slug>
 * and the OG meta carries the card image.
 */
export default function TrainingCards() {
  const { t, lang } = useT();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState("all");
  const [openCard, setOpenCard] = useState(null);

  // Deep-link: open modal if URL has a slug
  useEffect(() => {
    if (slug) {
      const card = findCardBySlug(slug);
      if (card) {
        setOpenCard(card);
      } else {
        navigate("/stoic-training-cards", { replace: true });
      }
    } else {
      setOpenCard(null);
    }
  }, [slug, navigate]);

  const visible = useMemo(() => filteredCards(active), [active]);

  const closeModal = () => {
    setOpenCard(null);
    if (slug) navigate("/stoic-training-cards", { replace: true });
  };

  const openModal = (card) => {
    setOpenCard(card);
    navigate(`/stoic-training-cards/${card.slug}`, { replace: false });
  };

  // SEO — when a slug is present we promote the card's image as the OG image
  const seoCard = slug ? findCardBySlug(slug) : null;
  const seoTitle = seoCard
    ? `${seoCard.attribution} · ${seoCard.theme[lang] || seoCard.theme.en} · Stoic Training Card`
    : "StoicOs Visual Library — Stoic Cards for Learning & Practice | StoicOs.ai";
  const seoDescription = seoCard
    ? seoCard.quote[lang] || seoCard.quote.en
    : t("cards.intro");
  const seoImage = seoCard ? `https://stoicos.ai${cardPublicUrl(seoCard)}` : undefined;
  const canonical = seoCard
    ? `https://stoicos.ai/stoic-training-cards/${seoCard.slug}`
    : "https://stoicos.ai/stoic-training-cards";

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical,
    og: {
      title: seoTitle,
      description: seoDescription,
      image: seoImage,
      type: seoCard ? "article" : "website",
    },
    jsonLd: [
      webPage({
        name: t("cards.title"),
        description: t("cards.intro"),
        url: "/stoic-training-cards",
      }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: t("cards.title"), url: "/stoic-training-cards" },
        ...(seoCard ? [{ name: `${seoCard.attribution} · ${seoCard.theme[lang] || seoCard.theme.en}`, url: canonical }] : []),
      ]),
    ],
  });

  return (
    <main data-testid="cards-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12"
          data-testid="cards-back"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs · Virtue Guide
        </Link>

        {/* Hero */}
        <header className="max-w-3xl">
          <p data-testid="cards-eyebrow" className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
            {t("cards.eyebrow")}
          </p>
          <h1
            data-testid="cards-title"
            className="font-inscription text-[2.25rem] sm:text-5xl md:text-6xl text-bone tracking-[0.06em] uppercase leading-[1.05]"
          >
            {t("cards.title")}
          </h1>
          <p className="mt-8 font-serif italic text-2xl md:text-3xl text-gold tracking-tight leading-tight">
            {t("cards.lede")}
          </p>
          <p className="mt-8 text-bone/85 font-reading text-lg md:text-xl leading-[1.85] max-w-2xl">
            {t("cards.intro")}
          </p>
          <div className="gold-rule w-40 mt-12" />
        </header>

        {/* StoicOs Cards — Concepts & Platform (indexable visual library) */}
        <StoicosCardSections />

        {/* Stoic Training series — Ed Korporaal (ranked by training number) */}
        <StoicTrainingSection />

        {/* Quote cards */}
        <section className="mt-20 md:mt-24" aria-labelledby="cards-quotes-heading">
          <h2 id="cards-quotes-heading" className="font-serif text-2xl md:text-3xl text-bone tracking-tight" data-testid="quote-cards-heading">{pickLang(QUOTE_SECTION.heading, lang)}</h2>
          <p className="mt-4 max-w-2xl text-bone/75 font-reading text-base md:text-lg leading-[1.85]">{pickLang(QUOTE_SECTION.blurb, lang)}</p>
        </section>

        {/* Category tabs */}
        <section className="mt-10" data-testid="cards-filter-bar">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
            {t("cards.browseLabel")}
          </p>
          <div className="flex flex-wrap gap-2.5" role="tablist" aria-label={t("cards.browseLabel")}>
            {CATEGORY_SLUGS.map((cat) => {
              const isActive = active === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`cards-tab-${cat}`}
                  onClick={() => {
                    setActive(cat);
                    trackCta("cards_filter", { category: cat });
                  }}
                  className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                    isActive
                      ? "border-gold text-gold bg-gold/5"
                      : "border-white/10 text-bone/75 hover:text-bone hover:border-white/25"
                  }`}
                >
                  {t(CATEGORY_LABEL_KEYS[cat])}
                </button>
              );
            })}
          </div>
          <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-fog/70">
            {visible.length} / {CARDS.length} {t("cards.shown")}
          </p>
        </section>

        {/* Grid */}
        <section className="mt-12" data-testid="cards-grid">
          {visible.length === 0 ? (
            <div
              data-testid="cards-empty"
              className="p-10 md:p-14 border border-white/8 bg-ink-elevated/40 text-center"
            >
              <p className="font-serif text-2xl md:text-3xl text-bone tracking-tight">
                {t("cards.empty.title")}
              </p>
              <p className="mt-4 max-w-xl mx-auto text-bone/75 font-reading text-base md:text-lg leading-[1.85]">
                {t("cards.empty.body")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {visible.map((card) => (
                <CardTile key={card.slug} card={card} onOpen={openModal} />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Lightbox */}
      {openCard && <CardModal card={openCard} onClose={closeModal} />}
    </main>
  );
}
